import { Node } from '@tiptap/core'

const QUICKBLOCK_LABELS = {
  cc: 'Chief Complaint',
  meds: 'Medications',
  allergies: 'Allergies',
  vitals: 'Vital Signs',
  medical_history: 'Medical History',
  surgical_history: 'Surgical History',
  family_history: 'Family History',
  social_history: 'Social History',
  gynecologic_history: 'Gynecologic History',
  problem_list: 'Problem List',
  immunizations: 'Immunizations',
}

export const SmartBlock = Node.create({
  name: 'smart_block',

  group: 'block',
  atom: true,
  selectable: true,
  isolating: true,
  defining: true,

  addAttributes() {
    return {
      key: { default: null },
      resolved: { default: false },
      snapshot: { default: null }, // STRUCTURED DATA
    }
  },

  parseHTML() {
    return [{ tag: 'smart-block' }]
  },

  renderHTML({ node }) {
    return [
      'smart-block',
      {
        'data-key': node.attrs.key,
        'data-resolved': node.attrs.resolved ? '1' : '0',
        contenteditable: 'false',
        class: node.attrs.resolved
          ? 'smart_block smart_block_resolved'
          : 'smart_block',
      },
    ]
  },

  addNodeView() {
    return ({ node, getPos, editor }) => {
      const dom = document.createElement('smart-block')
      dom.contentEditable = 'false'

      const content = document.createElement('div')
      content.className = 'smart_block_content'
      dom.appendChild(content)

      function render(currentNode) {
        dom.className = currentNode.attrs.resolved
          ? 'smart_block smart_block_resolved'
          : 'smart_block'

        content.innerHTML = ''

        // Remove button (only if unresolved)
        if (!currentNode.attrs.resolved) {
          const remove = document.createElement('button')
          remove.className = 'smart_block_remove'
          remove.textContent = '×'
          remove.title = 'Remove QuickBlock'

          remove.addEventListener('click', e => {
            e.preventDefault()
            e.stopPropagation()

            const pos = getPos()
            if (typeof pos === 'number') {
              editor
                .chain()
                .focus()
                .deleteRange({ from: pos, to: pos + currentNode.nodeSize })
                .run()
            }
          })

          dom.appendChild(remove)
        }

        // Content
        if (currentNode.attrs.resolved && currentNode.attrs.snapshot) {
          renderSnapshot(content, currentNode.attrs.snapshot, currentNode.attrs.key)
        } else {
          content.textContent =
            QUICKBLOCK_LABELS[currentNode.attrs.key] || 'QuickBlock'
        }
      }

      // Initial render
      render(node)

      return {
        dom,

        update(updatedNode) {
          if (updatedNode.type !== node.type) return false
          node = updatedNode
          render(node)
          return true
        }
      }
    }
  }

})

function renderSnapshot(container, snapshot, blockKey = '')
{
  if (!snapshot || !snapshot.render) return;

  // Clear container (safety)
  container.innerHTML = '';

  switch (snapshot.render)
  {
    case 'cc':
      renderChiefComplaint(container, snapshot.items);
      break;

    case 'list':
      if (blockKey === 'meds')
        renderMedications(container, snapshot.items);
      else
        renderUnorderedList(container, snapshot.items);
      break;

    case 'medications':
      renderMedications(container, snapshot.items);
      break;

    case 'key_value':
      renderKeyValue(container, snapshot.items);
      break;

    case 'table':
      renderTable(container, snapshot.columns, snapshot.rows);
      break;

    default:
      renderFallback(container, snapshot.items);
  }
}


function renderChiefComplaint(container, items = [])
{
  container.classList.add('smart_block_cc');

  items.forEach((item, idx) => {
    const row = document.createElement('div');
    row.className = idx === 0
      ? 'smart_block_cc_primary'
      : 'smart_block_cc_secondary';

    row.textContent = item.value || '';
    container.appendChild(row);
  });
}

function renderUnorderedList(container, items = [])
{
  const ul = document.createElement('ul');
  ul.className = 'smart_block_list';

  items.forEach(item => {
    const li = document.createElement('li');
    li.textContent = typeof item === 'string' ? item : (item.value || '');
    ul.appendChild(li);
  });

  container.appendChild(ul);
}

function renderMedications(container, items = [])
{
  const table = document.createElement('table');
  table.className = 'smartblock_table smartblock_medication_table';
  const tbody = document.createElement('tbody');

  items.forEach(item =>
  {
    const medication = typeof item === 'string' ? { name: item } : item;
    const name = medication.name || medication.value || '';
    if (!name) return;

    const row = document.createElement('tr');
    const cell = document.createElement('td');

    [name, medication.directions, medication.prescribed_date]
      .filter(Boolean)
      .forEach(value =>
      {
        const line = document.createElement('div');
        line.textContent = value;
        cell.appendChild(line);
      });

    row.appendChild(cell);
    tbody.appendChild(row);
  });

  table.appendChild(tbody);
  container.appendChild(table);
}

function renderKeyValue(container, items = [])
{
  items.forEach(item => {
    const row = document.createElement('div');
    row.className = 'smart_block_row';

    const label = document.createElement('strong');
    label.textContent = item.label + ': ';

    row.appendChild(label);
    row.appendChild(document.createTextNode(item.value || ''));

    container.appendChild(row);
  });
}

function renderTable(container, columns = [], rows = [])
{
  const table = document.createElement('table');
  table.className = 'smartblock_table';

  // ✅ Only render header if at least one label exists
  const hasHeader = columns.some(col => col.label && col.label.trim() !== '');

  if (hasHeader)
  {
    const thead = document.createElement('thead');
    const trHead = document.createElement('tr');

    columns.forEach(col => 
    {
      const th = document.createElement('th');
      th.textContent = col.label ? col.label : '';
      trHead.appendChild(th);
    });

    thead.appendChild(trHead);
    table.appendChild(thead);
  }

  const tbody = document.createElement('tbody');

  rows.forEach(row =>
  {
    const tr = document.createElement('tr');

    columns.forEach(col =>
    {
      const td = document.createElement('td');
      const cell = row[col.key];

      if (typeof cell === 'string' || cell === null)
      {
        td.textContent = cell ?? '';
      }
      else if (cell.type === 'list')
      {
        renderUnorderedList(td, cell.items);
      }
      else if (cell.type === 'stack')
      {
        cell.items.forEach(item =>
        {
          const div = document.createElement('div');
          div.textContent = item.value ?? '';
          td.appendChild(div);
        });
      }
      else
      {
        td.textContent = '';
      }

      tr.appendChild(td);
    });

    tbody.appendChild(tr);
  });

  table.appendChild(tbody);
  container.appendChild(table);
}





function renderFallback(container, items = [])
{
  items.forEach(item => {
    const div = document.createElement('div');
    div.textContent = item.value || '';
    container.appendChild(div);
  });
}
